/**
 * "Is a document source open right now?" — the ONE read-only signal the chat
 * surfaces use to tell the truth about an imported document.
 *
 * Measured 2026-07-30: an imported `.docx` is invisible to generation. The
 * viewer parses it in the browser and keeps the result in a module-scoped
 * session (`features/source-document/docx-source-viewer-state.ts`); nothing
 * persists it, and no generation or chat service reads it. `@cogo/source-document`
 * ships a `cogo.source-ingest-bundle.v1` builder (`buildDocxIngestBundle`) for
 * exactly this hand-off, but it has ZERO callers — the ingest → ontology →
 * retrieval span it was designed for
 * (`docs/agentic/COGO-DOCUMENT-ONTOLOGY-INGEST-TO-PUBLISHING-ARCHITECTURE-2026-07-22.md`
 * stage C) is not built.
 *
 * So a request that cites an open document CANNOT be grounded on it. This
 * module exists so the chat rail can say that in the user's own words BEFORE
 * they spend a build on it, instead of quietly generating something unrelated.
 *
 * Read-only by construction: it imports the viewer session's public accessors
 * and never writes to them. Document import, persistence, and the viewer itself
 * are owned elsewhere; this file must not grow a setter.
 */

import { useSyncExternalStore } from 'react'
import {
  getDocxViewerSession,
  subscribeDocxViewerSession,
} from '@/features/source-document/docx-source-viewer-state'

export interface OpenDocumentSource {
  /** File name as the user imported it (already on screen in the viewer). */
  fileName: string
}

/**
 * The document currently open in this browser session, or `null`.
 *
 * Only a SUCCESSFULLY parsed document counts: a failed parse is already
 * surfaced by the viewer's own error banner and must not make chat claim a
 * source exists.
 */
export function readOpenDocumentSource(): OpenDocumentSource | null {
  const model = getDocxViewerSession().model
  if (!model || !model.ok) return null
  return { fileName: model.fileName }
}

/** React binding for {@link readOpenDocumentSource}. */
export function useOpenDocumentSource(): OpenDocumentSource | null {
  return useSyncExternalStore(
    subscribeDocxViewerSession,
    readOpenDocumentSourceSnapshot,
    readNullSnapshot,
  )
}

// `useSyncExternalStore` compares snapshots by identity, so the getter must
// return a stable reference while the underlying session is unchanged.
let cachedFileName: string | null = null
let cachedSnapshot: OpenDocumentSource | null = null

function readOpenDocumentSourceSnapshot(): OpenDocumentSource | null {
  const next = readOpenDocumentSource()
  if (!next) {
    cachedFileName = null
    cachedSnapshot = null
    return null
  }
  if (cachedSnapshot && cachedFileName === next.fileName) return cachedSnapshot
  cachedFileName = next.fileName
  cachedSnapshot = next
  return cachedSnapshot
}

/** Server snapshot: there is never an open document during SSR/hydration. */
function readNullSnapshot(): OpenDocumentSource | null {
  return null
}
